import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { toggleAnimation } from "src/app/shared/animations";
import { BaseTableComponent, DatatableColumn } from "src/app/shared/components/base-table/base-table.component";
import { InstanceRequest, InstanceRequestState } from "../instance-requests";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/index.selector";
import { TranslateService } from "@ngx-translate/core";
import { TableService } from "src/app/shared/services/table.service";
import { MessagePortalComponent } from "src/app/portal/portal";
import { InstanceRequestService } from "src/app/shared/services/instance-requests.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable, take } from "rxjs";
import { Package } from "src/app/shared/types/contract-types";
import { DomainNameValidators } from "src/app/shared/validators/domain-name.validator";
import { PackageService } from "src/app/shared/services/package.service";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { PopupType } from "src/app/shared/enums/popup-types";

@Component({
  selector: "app-instance-requests-table",
  templateUrl: "./instance-requests.table.component.html",
  standalone: false,
  animations: [toggleAnimation],
})
export class InstanceRequestsTable extends BaseTableComponent<InstanceRequest> implements OnInit {
  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;
  @ViewChild("approveModalTemplate") approveModalTemplate!: TemplateRef<unknown>;
  @ViewChild("rejectModalTemplate") rejectModalTemplate!: TemplateRef<unknown>;
  @ViewChild("deleteConfirmationTemplate") deleteConfirmationTemplate!: TemplateRef<unknown>;
  public options$!: Observable<Package[]>;

  approveForm!: FormGroup;
  isLoading = false;
  isLoadingDelete = false;
  selectedId!: number | null;
  InstanceRequestState = InstanceRequestState;

  public rejectComment = "";

  approveModal!: DialogRef;
  rejectModal!: DialogRef;
  deleteModal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(
    public override translate: TranslateService,
    public override store: Store<AppState>,
    public override tableService: TableService,
    private positioningService: PositioningService,
    private instanceRequestService: InstanceRequestService,
    private fb: FormBuilder,
    private packageService: PackageService,
    private domainValidators: DomainNameValidators,
  ) {
    super(store, tableService, `InstanceRequests/paginated`, translate);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.getColumns();
  }

  getColumns(): void {
    this.cols = [
      { field: "id", title: "Id", sort: true, hide: true } as DatatableColumn,
      { field: "email", title: this.translate.instant("contract.email"), sort: true } as DatatableColumn,
      { field: "actions", title: this.translate.instant("contract.actions"), sort: false } as DatatableColumn,
    ];
  }

  initApproveForm(): void {
    this.options$ = this.packageService.getDefaultPackage();
    this.approveForm = this.fb.group({
      agencyName: ["", Validators.required],
      phoneNumber: ["", Validators.required],
      domainName: [
        "",
        [Validators.required, DomainNameValidators.patternValidator()],
      ],
      tier: ["", Validators.required],
    });
    this.options$.pipe(take(1)).subscribe((options) => {
      if (options?.length) {
        this.approveForm.get("tier")?.setValue(options[options.length - 1].id);
      }
    });
  }

  openApproveModal(id: number): void {
    this.initApproveForm();
    this.selectedId = id;
    this.approveModal = this.dialog.open(this.approveModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeApproveModal(): void {
    this.selectedId = null;
    this.approveModal.close();
  }

  validateDomain() {
    const control = this.approveForm.get("domainName");
    if (!control)
      return;

    control.setAsyncValidators(
      this.domainValidators.domainAvailabilityValidator(),
    );
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  submitApproveForm() {
    if (!this.approveForm.valid) {
      this.approveForm.markAllAsTouched();
      return;
    }

    if (this.selectedId) {
      this.isLoading = true;
      const subscription = this.instanceRequestService.approveInstanceRequest(this.selectedId, this.approveForm.getRawValue()).subscribe({
        next: () => {
          subscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Success, this.translate.instant("instance_request.approve_successfull"));
          this.closeApproveModal();
          this.selectedId = null;
          this.fetchItems(this.getPaginationParams());
        },
        error: (err) => {
          subscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Danger, err.error);
        },
      });
    }
  }

  openRejectModal(id: number): void {
    this.rejectComment = "";
    this.selectedId = id;
    this.rejectModal = this.dialog.open(this.rejectModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeRejectModal(): void {
    this.selectedId = null;
    this.rejectModal.close();
  }

  handleReject() {
    if (this.selectedId) {
      this.isLoading = true;
      const subscription = this.instanceRequestService.rejectInstanceRequest(this.selectedId, this.rejectComment).subscribe({
        next: () => {
          subscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Success, this.translate.instant("instance_request.reject_successfull"));
          this.closeRejectModal();
          this.selectedId = null;
          this.fetchItems(this.getPaginationParams());
        },
        error: (err) => {
          subscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Danger, err.error);
        },
      });
    }
  }

  openDeleteModal(id: number): void {
    this.selectedId = id;
    this.deleteModal = this.dialog.open(this.deleteConfirmationTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeDeleteModal() {
    this.selectedId = null;
    this.deleteModal.close();
  }

  handleDelete() {
    if (this.selectedId) {
      this.isLoadingDelete = true;
      const subscription = this.instanceRequestService.deleteInstanceRequest(this.selectedId).subscribe({
        next: () => {
          subscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Success, this.translate.instant("instance_request.delete_successfull"));
          this.closeDeleteModal();
          this.selectedId = null;
          this.fetchItems(this.getPaginationParams());
        },
        error: (err) => {
          subscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Danger, err.error);
        },
      });
    }
  }

  setSearchText(text: string) {
    setTimeout(() => {
      this.search = text;
    });
  }

  onPortalAttached() {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }
}
